import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/DashboardHome.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SecurityHandler from './components/common/SecurityHandler';
import Faq from './components/home/FAQSection.jsx';
import AboutUs from './pages/AboutUs.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import StudentForm from './pages/studentform';


// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import AdminTeachers from './pages/admin/AdminTeachers';
import AdminClasses from './pages/admin/AdminClasses';
import AdminSettings from './pages/admin/AdminSettings';
import AdminHistory from './pages/admin/AdminHistory';
import AdminPayments from './pages/admin/AdminPayments';
import AdminMarks from './pages/admin/AdminMarks';
import AdminFeedback from './pages/admin/AdminFeedback';
import AdminAttendance from './pages/admin/AdminAttendance';
import AdminEvaluation from './pages/admin/AdminEvaluation';
import AdminDelivery from './pages/admin/AdminDelivery';

// Student Pages
import StudentCourses from './pages/student/StudentCourses';
import StudentTeacherDetail from './pages/student/StudentTeacherDetail';
import StudentCourseView from './pages/student/StudentCourseView';
import StudentLessonView from './pages/student/StudentLessonView';
import StudentPayment from './pages/student/StudentPayment';
import StudentMarks from './pages/student/StudentMarks';
import StudentAttendance from './pages/student/StudentAttendance';
import ForgotPassword from './pages/ForgotPassword';
import StudentFeedback from './pages/StudentFeedback';
import WaitingRoom from './pages/WaitingRoom';
import Unauthorized from './pages/Unauthorized';
import NotFound from './pages/NotFound';

// Common Pages
import Profile from './pages/common/Profile';

// Helper components for Role-Based Views
const RoleBasedPayment = () => {
  const { currentUser } = useAuth();

  if (!currentUser) return <Navigate to="/login" replace />;

  if (currentUser.role === 'admin') {
    return <AdminPayments />;
  }
  return <StudentPayment />;
};

const RoleBasedMarks = () => {
  const { currentUser } = useAuth();
  return (currentUser.role === 'admin' || currentUser.role === 'moderator') ? <AdminMarks /> : <StudentMarks />;
};

function App() {
  return (
    <AuthProvider>
      <Toaster />
      <SecurityHandler />
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/waiting-room" element={<WaitingRoom />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/feedback" element={<StudentFeedback />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/studentform" element={<StudentForm />} />
          

          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            {/* Common Dashboard Home */}
            <Route index element={<Dashboard />} />

            {/* Common Profile */}
            <Route path="profile" element={<Profile />} />

            {/* Admin Routes */}
            <Route path="users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserManagement />
              </ProtectedRoute>
            } />

            <Route path="admin/teachers" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminTeachers />
              </ProtectedRoute>
            } />
            <Route path="admin/classes" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminClasses />
              </ProtectedRoute>
            } />
            <Route path="admin/settings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminSettings />
              </ProtectedRoute>
            } />
            <Route path="admin/history" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminHistory />
              </ProtectedRoute>
            } />
            <Route path="admin/payments" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPayments />
              </ProtectedRoute>
            } />
            <Route path="admin/marks" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminMarks />
              </ProtectedRoute>
            } />
            <Route path="admin/feedback" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminFeedback />
              </ProtectedRoute>
            } />
            <Route path="admin/attendance" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminAttendance />
              </ProtectedRoute>
            } />
            <Route path="admin/user" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminFeedback />
              </ProtectedRoute>
            } />
            <Route path="admin/evaluation" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminEvaluation />
              </ProtectedRoute>
            } />
            <Route path="admin/delivery" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDelivery />
              </ProtectedRoute>
            } />

            {/* Role-Based Routes (Shared Paths) */}
            <Route path="payments" element={
              <ProtectedRoute allowedRoles={['admin', 'student']}>
                <RoleBasedPayment />
              </ProtectedRoute>
            } />

            <Route path="marks" element={
              <ProtectedRoute allowedRoles={['admin', 'moderator', 'student']}>
                <RoleBasedMarks />
              </ProtectedRoute>
            } />

            {/* Student Specific Routes */}
            <Route path="courses" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentCourses />
              </ProtectedRoute>
            } />
            <Route path="courses/teacher/:teacherId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentTeacherDetail />
              </ProtectedRoute>
            } />
            <Route path="courses/class/:classId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentCourseView />
              </ProtectedRoute>
            } />
            <Route path="courses/class/:classId/lesson/:monthId" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentLessonView />
              </ProtectedRoute>
            } />
            <Route path="student/attendance" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentAttendance />
              </ProtectedRoute>
            } />

          </Route>
      

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
